import { Link } from "react-router-dom";
import { Gem, Flame, Sofa, Ruler, AlignHorizontalJustifyCenter, Bell, Music, Droplet, Wind, ThermometerSun, BedDouble, Home as HomeIcon, Building2 } from "lucide-react";
import { COMFORT_TOOLS } from "@/data/comfortTools";
import { THERAPIST } from "@/data/catalog";
import useSEO from "@/hooks/useSEO";
import ReserveButton from "@/components/ReserveButton";
import { Button } from "@/components/ui/button";
import "@/styles/aufguss.css";

const ICON_MAP = {
    Gem, Flame, Sofa, Ruler, AlignHorizontalJustifyCenter,
    Bell, Music, Droplet, Wind, ThermometerSun, BedDouble,
};

// Petit rendu de la vague Aufguss — SVG animé pur CSS.
function AufgussAnimation() {
    return (
        <div data-testid="aufguss-animation" className="aufguss-stage" aria-hidden="true">
            <div className="aufguss-orb" />
            <div className="aufguss-wave aufguss-wave-1" />
            <div className="aufguss-wave aufguss-wave-2" />
            <div className="aufguss-wave aufguss-wave-3" />
            <div className="aufguss-towel">
                <svg viewBox="0 0 120 40" preserveAspectRatio="none">
                    <path
                        d="M4 20 Q30 4 60 20 T116 20"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeLinecap="round"
                        fill="none"
                    />
                </svg>
            </div>
        </div>
    );
}

export default function ComfortTools() {
    useSEO({
        title: "Confort & outils utilisés · Jérémy Tessier, Massothérapeute Blainville",
        description:
            "Découvrez le matériel utilisé en séance : table Master, guashas de jade, pad chauffant, bâton thaï, bol tibétain, tongue drum, traversins. À domicile : huiles essentielles, encens et expérience Aufguss.",
        canonical: "https://massoclic.ca/confort-et-outils",
        ogImage: "https://massoclic.ca/icon.svg",
    });

    const clinique = COMFORT_TOOLS.filter((t) => t.context.startsWith("Clinique"));
    const domicile = COMFORT_TOOLS.filter((t) => !t.context.startsWith("Clinique") && t.id !== "aufguss");
    const aufguss = COMFORT_TOOLS.find((t) => t.id === "aufguss");

    const renderCard = (tool, isAccent = false) => {
        const Icon = ICON_MAP[tool.icon];
        return (
            <article
                key={tool.id}
                data-testid={`comfort-tool-${tool.id}`}
                className={`rounded-3xl overflow-hidden shadow-[0_8px_30px_rgba(44,42,41,0.04)] ${
                    isAccent
                        ? "border-2 border-[#E07A5F]/40 bg-white"
                        : "border border-[#E8E2D9] bg-white"
                }`}
            >
                {tool.image && (
                    <div className="aspect-[16/10] w-full overflow-hidden bg-[#FAF7F2]">
                        <img
                            src={tool.image}
                            alt={tool.name}
                            loading="lazy"
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}
                <div className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                        {Icon && (
                            <div className={`p-2 rounded-xl ${isAccent ? "bg-[#E07A5F]/10" : "bg-[#3D8C95]/10"}`}>
                                <Icon size={20} className={isAccent ? "text-[#E07A5F]" : "text-[#3D8C95]"} />
                            </div>
                        )}
                        <div>
                            <h3 className="font-display text-xl text-[#2C2A29] leading-tight">{tool.name}</h3>
                            <div className={`text-[11px] uppercase tracking-widest mt-0.5 ${isAccent ? "text-[#E07A5F]" : "text-[#6E6862]"}`}>
                                {tool.context}
                            </div>
                        </div>
                    </div>
                    <p className="text-sm text-[#6E6862] leading-relaxed">{tool.description}</p>
                </div>
            </article>
        );
    };

    return (
        <main data-testid="comfort-tools-page" className="pt-32 md:pt-40 pb-20">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
                {/* Hero */}
                <div className="mb-14">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#E07A5F] mb-4">
                        Confort & outils utilisés en séance
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        Le matériel qui{" "}
                        <span className="italic text-[#E07A5F]">travaille avec moi</span>.
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed max-w-3xl">
                        Chaque outil a été choisi pour une raison précise — confort du corps, précision du travail manuel, apaisement du système nerveux, immersion sensorielle. Voici la liste complète, en photos, de ce que j'utilise en cabinet et lors des soins à domicile.
                    </p>
                    <div className="mt-6">
                        <ReserveButton />
                    </div>
                </div>

                {/* Section 1 — En clinique & à domicile */}
                <section className="mb-16">
                    <div className="flex items-baseline gap-3 mb-6">
                        <Building2 size={24} className="text-[#3D8C95]" />
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">
                            En clinique <span className="italic text-[#3D8C95]">& à domicile</span>
                        </h2>
                    </div>
                    <p className="text-sm text-[#6E6862] mb-6">
                        Ces outils m'accompagnent partout — au cabinet de Blainville comme lors des déplacements dans les Laurentides.
                    </p>
                    <div className="grid md:grid-cols-2 gap-5">
                        {clinique.map((t) => renderCard(t, false))}
                    </div>
                </section>

                {/* Section 2 — Domicile uniquement */}
                <section className="mb-16">
                    <div className="flex items-baseline gap-3 mb-6">
                        <HomeIcon size={24} className="text-[#E07A5F]" />
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">
                            À domicile <span className="italic text-[#E07A5F]">uniquement</span>
                        </h2>
                    </div>
                    <p className="text-sm text-[#6E6862] mb-6">
                        Une dimension sensorielle supplémentaire que je propose uniquement dans le confort de votre foyer — l'espace vous appartient, l'ambiance se construit avec vous.
                    </p>
                    <div className="grid md:grid-cols-2 gap-5">
                        {domicile.map((t) => renderCard(t, true))}
                    </div>
                </section>

                {/* Section 3 — Aufguss immersion (animation + photo) */}
                {aufguss && (
                    <section id="aufguss" className="mb-16 scroll-mt-24">
                        <div className="rounded-3xl overflow-hidden border-2 border-[#E07A5F]/40 bg-gradient-to-br from-[#E07A5F]/5 via-white to-[#3D8C95]/5">
                            <div className="grid md:grid-cols-2 gap-0">
                                <div className="relative bg-[#2C2A29] overflow-hidden min-h-[280px] md:min-h-[420px]">
                                    <picture>
                                        <source srcSet={aufguss.image} type="image/webp" />
                                        <img
                                            src={aufguss.imageFallback || aufguss.image}
                                            alt="Rituel Aufguss à domicile"
                                            className="absolute inset-0 w-full h-full object-cover opacity-60"
                                            loading="lazy"
                                            width="1400"
                                            height="764"
                                        />
                                    </picture>
                                    <AufgussAnimation />
                                </div>
                                <div className="p-7 md:p-9">
                                    <span className="inline-block text-[10px] uppercase tracking-widest bg-[#E07A5F]/15 text-[#E07A5F] rounded-full px-3 py-1 mb-4">
                                        Expérience Signature à domicile
                                    </span>
                                    <h3 className="font-display text-2xl md:text-3xl text-[#2C2A29] leading-tight mb-3">
                                        L&apos;Aufguss — <span className="italic text-[#E07A5F]">vagues d&apos;air parfumé</span>
                                    </h3>
                                    <p className="text-sm text-[#6E6862] leading-relaxed mb-4">
                                        {aufguss.description}
                                    </p>
                                    <div className="text-[11px] uppercase tracking-widest text-[#E07A5F] mb-4">
                                        {aufguss.context}
                                    </div>
                                    <Link
                                        to="/blog/aufguss-a-domicile-massage-psychocorporel"
                                        className="text-sm text-[#3D8C95] hover:text-[#2C6D75] underline underline-offset-4 inline-flex items-center gap-1"
                                    >
                                        Lire l&apos;article complet →
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* CTA */}
                <div className="rounded-3xl bg-[#2C2A29] text-white p-8 md:p-10 text-center">
                    <h2 className="font-display text-3xl md:text-4xl">
                        Une expérience pensée <span className="italic text-[#E07A5F]">dans chaque détail</span>
                    </h2>
                    <p className="mt-4 text-white/80 max-w-2xl mx-auto text-sm md:text-base">
                        Réservez votre séance en clinique à Blainville ou à domicile dans les Laurentides et Rive-Nord. Précisez vos préférences (huiles, ambiance, Aufguss) au moment de la demande.
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                        <Link to="/#reservation">
                            <Button
                                data-testid="comfort-cta-book"
                                className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6"
                            >
                                Demander un rendez-vous
                            </Button>
                        </Link>
                        <a href={`tel:${THERAPIST.phone}`}>
                            <Button variant="outline" className="rounded-full border-white text-white hover:bg-white hover:text-[#2C2A29] px-8 py-6">
                                {THERAPIST.phone}
                            </Button>
                        </a>
                    </div>
                </div>
            </div>
        </main>
    );
}
